import React, {FC} from 'react';

import {Box, Checkbox, Typography} from '@mui/material';
import {t} from 'i18next';

import {AlcoDeliveryInfoIcon} from 'assets/icons/AlcoDeliveryInfoIcon';
import {ExpressSelectorOptionIcon} from 'assets/icons/expressSelectorOptionIcon';
import MakroProIcon from 'assets/icons/makro-pro';
import NewSellerIcon from 'assets/icons/newSeller';
import theme from 'config/theme';
import {formatPriceWithCurrency} from 'utils/currency';

import {BasketDeliveryFee} from './BasketDeliveryFee';
import {
  BasketDeliveryInfo1P,
  BasketDeliveryInfo3P as BasketDeliveryInfo,
  BasketDeliveryInfoMakroFleet
} from './BasketDeliveryInfo';
import {StyledBasketSellerHeader, StyledBasketSellerName, StyledBasketLabel, StyledShopLogo} from './styles';

interface BasketSellerHeaderProps {
  onClick: () => void;
  subtotal: number;
  priceUnit: string;
  isSelectedAllBasketItems: boolean;
  isAllItemsFromThisSectionSelected: boolean;
  isReactNativeView?: boolean;
}

export interface BasketSellerHeader1PProps extends BasketSellerHeaderProps {
  currentShippingFee: number;
  defaultShippingFee: number;
  deliveryBy: string;
  onlyBulkyItems: boolean;
  onlyPreOrderItems: boolean;
  onSelectSection: (e: any) => void;
  description?: string;
}

export interface BasketSellerHeader3PProps extends BasketSellerHeaderProps {
  deliveryBy: string;
  sellerName: string;
  shippingFee: number;
  originalShippingFee?: number;
  onSelectSection: (e: any) => void;
  showDeliveryFee?: boolean;
  shopLogo?: string;
}

export interface BasketSellerHeaderMakroFleetProps extends BasketSellerHeaderProps {
  sellerName: string;
  shippingFee: number;
  description: string;
  onSelectSection: (e: any) => void;
}

export interface BasketSellerHeaderBulkyProps {
  title: string;
  onClick: () => void;
  deliveryBy: string;
  onlyBulkyItems: boolean;
  only1PxBulkyItems: boolean;
  onSelectSection: (e: any) => void;
  is1PSeller?: boolean;
  is1PX?: boolean;
  isSelectedAllBasketItems: boolean;
  isAllItemsFromThisSectionSelected: boolean;
  isReactNativeView?: boolean;
}
export interface BasketSellerHeaderPreOrderProps {
  onClick: () => void;
  title: string;
  deliveryBy: string;
  onlyPreOrderItems: boolean;
  onSelectSection: (e: any) => void;
  isSelectedAllBasketItems: boolean;
  isAllItemsFromThisSectionSelected: boolean;
  isReactNativeView?: boolean;
  hidePreOrderDates?: boolean;
}

export interface BasketSellerHeaderAlcoProps {
  title: string;
  onClick: () => void;
  message: string;
  minimumQuantityError?: string;
  onSelectSection: (e: any) => void;
  isSelectedAllBasketItems: boolean;
  isAllItemsFromThisSectionSelected: boolean;
  onClickDeliveryInfo: () => void;
  isUnavailable?: boolean;
  isReactNativeView?: boolean;
}

export interface BasketExpressHeaderProps extends BasketSellerHeaderProps {
  shippingFee: number;
  originalShippingFee?: number;
  onSelectSection: (e: any) => void;
}

const checkBoxComponent = (props: any) => {
  return (
    <Box>
      <Checkbox
        name="isDefault"
        disabled={props.disabled}
        defaultChecked={props.defaultChecked}
        data-test-id={`${props.sectionName}_seller_header_checkbox`}
        sx={{
          '&.Mui-disabled': {color: theme.palette.primary.light},
          color: '#C7C7C7',
          '&.Mui-checked': {
            color: 'primary'
          },
          height: 'fit-content',
          padding: 0
        }}
        {...props}
      />
    </Box>
  );
};

// In flexible basket this header will not render for other than 1p e.g bulky section
export const BasketSellerHeader1P: FC<BasketSellerHeader1PProps> = (props) => {
  return (
    <StyledBasketSellerHeader isReactNativeView={props.isReactNativeView}>
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center">
          {checkBoxComponent({
            defaultChecked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            checked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            onChange: props.onSelectSection,
            sectionName: '1p'
          })}
          &nbsp;&nbsp;
          <MakroProIcon />
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
        {props.onlyBulkyItems && !!props.deliveryBy ? (
          <BasketDeliveryInfoMakroFleet
            message={props.deliveryBy}
            onClick={props.onClick}
            dataTestId={'basket_bulky_delivery_info'}
          />
        ) : (
          <>
            {props.onlyPreOrderItems && !!props.deliveryBy ? (
              <BasketDeliveryInfo
                message={props.deliveryBy}
                onClick={props.onClick}
                dataTestId={'basket_pre_order_delivery_info'}
              />
            ) : (
              <BasketDeliveryInfo1P onClick={props.onClick} message={props.description} />
            )}
          </>
        )}
      </Box>
    </StyledBasketSellerHeader>
  );
};

export const BasketSellerHeader3P: FC<BasketSellerHeader3PProps> = (props) => {
  return (
    <StyledBasketSellerHeader isReactNativeView={props.isReactNativeView}>
      <Box display="flex" width="100%">
        <Box display="flex" alignItems="center">
          {checkBoxComponent({
            defaultChecked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            checked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            onChange: props.onSelectSection,
            sectionName: '3p'
          })}
          &nbsp;&nbsp;
          {props.shopLogo ? (
            <StyledShopLogo>
              <img width="20px" height="20px" src={props.shopLogo} alt="seller logo" />
            </StyledShopLogo>
          ) : (
            <NewSellerIcon />
          )}
        </Box>
        <Box
          display="flex"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          paddingLeft="8px"
        >
          <StyledBasketSellerName>{props.sellerName}</StyledBasketSellerName>
          {!!props.showDeliveryFee && (
            <Box display="flex" alignItems="center" justifyContent={'space-between'}>
              <StyledBasketLabel>{t('label.deliveryFee')}:</StyledBasketLabel>
              &nbsp;
              <BasketDeliveryFee
                currentShippingFee={props.shippingFee}
                defaultShippingFee={props.originalShippingFee}
                priceUnit={props.priceUnit}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
        <BasketDeliveryInfo message={props.deliveryBy} onClick={props.onClick} />
      </Box>
    </StyledBasketSellerHeader>
  );
};

export const BasketSellerHeaderMakroFleet: FC<BasketSellerHeaderMakroFleetProps> = (props) => {
  return (
    <StyledBasketSellerHeader isReactNativeView={props.isReactNativeView}>
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center" flexWrap="wrap">
          {checkBoxComponent({
            defaultChecked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            checked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            onChange: props.onSelectSection,
            sectionName: 'makro_fleet'
          })}
          &nbsp;&nbsp;
          <MakroProIcon />
          &nbsp;&nbsp;
          <StyledBasketSellerName>{props.sellerName}</StyledBasketSellerName>
        </Box>
        <Box display="flex" alignItems="center" fontSize="0.8rem">
          <StyledBasketLabel>{t('label.subtotal')}:</StyledBasketLabel>
          &nbsp;
          <Typography fontSize="0.9rem" fontWeight={700}>
            {formatPriceWithCurrency(props.subtotal, props.priceUnit)}
          </Typography>
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
        <BasketDeliveryInfoMakroFleet message={props.description} onClick={props.onClick} />
        <Box display="flex" alignItems="center">
          <StyledBasketLabel>{t('label.deliveryFee')}:</StyledBasketLabel>
          &nbsp;
          <BasketDeliveryFee currentShippingFee={props.shippingFee} priceUnit={props.priceUnit} />
        </Box>
      </Box>
    </StyledBasketSellerHeader>
  );
};

export const BasketSellerHeaderBulky: FC<BasketSellerHeaderBulkyProps> = (props) => {
  return (
    <StyledBasketSellerHeader
      isReactNativeView={props.isReactNativeView}
      enableTopBorder={props.onlyBulkyItems || props.only1PxBulkyItems}
    >
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center" flexWrap="wrap">
          {checkBoxComponent({
            defaultChecked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            checked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            onChange: props.onSelectSection,
            sectionName: 'bulky'
          })}
          &nbsp;&nbsp;
          <MakroProIcon />
          &nbsp;&nbsp;
          <StyledBasketSellerName>{props.title}</StyledBasketSellerName>
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
        <BasketDeliveryInfoMakroFleet
          message={props.deliveryBy}
          onClick={props.onClick}
          dataTestId={'basket_bulky_delivery_info'}
        />
      </Box>
    </StyledBasketSellerHeader>
  );
};

export const BasketSellerHeaderPreOrder: FC<BasketSellerHeaderPreOrderProps> = (props) => {
  return (
    <StyledBasketSellerHeader isReactNativeView={props.isReactNativeView} enableTopBorder={props.onlyPreOrderItems}>
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center" flexWrap="wrap">
          {checkBoxComponent({
            defaultChecked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            checked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            onChange: props.onSelectSection,
            sectionName: 'pre_order'
          })}
          &nbsp;&nbsp;
          <MakroProIcon />
          &nbsp;&nbsp;
          <StyledBasketSellerName>{props.title}</StyledBasketSellerName>
        </Box>
      </Box>

      {!props.hidePreOrderDates && (
        <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
          <BasketDeliveryInfo
            message={props.deliveryBy}
            onClick={props.onClick}
            dataTestId={'basket_pre_order_delivery_info'}
          />
        </Box>
      )}
    </StyledBasketSellerHeader>
  );
};

export const BasketSellerHeaderAlco: FC<BasketSellerHeaderAlcoProps> = (props) => (
  <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
    <StyledBasketSellerHeader isReactNativeView={props.isReactNativeView}>
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center" flexWrap="wrap">
          {checkBoxComponent({
            defaultChecked: props.isUnavailable
              ? false
              : props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            checked: props.isUnavailable
              ? false
              : props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            onChange: props.onSelectSection,
            disabled: props.isUnavailable,
            sectionName: 'alco'
          })}
          &nbsp;&nbsp;
          <MakroProIcon />
          &nbsp;&nbsp;
          <StyledBasketSellerName>{props.title}</StyledBasketSellerName>
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
        <BasketDeliveryInfo1P
          onClick={props.onClickDeliveryInfo}
          message={props?.message}
          dataTestId={'basket_alco_delivery_info'}
        />
      </Box>
    </StyledBasketSellerHeader>
    <Box sx={{maxHeight: '20px', paddingRight: 2, cursor: 'pointer'}} onClick={props.onClick}>
      {<AlcoDeliveryInfoIcon />}
    </Box>
  </Box>
);

export const BasketExpressHeader: FC<BasketExpressHeaderProps> = (props) => {
  return (
    <StyledBasketSellerHeader isReactNativeView={props.isReactNativeView} data-test-id={'express-section-header'}>
      <Box display="flex" width="100%">
        <Box display="flex" alignItems="center">
          {checkBoxComponent({
            defaultChecked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            checked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
            onChange: props.onSelectSection,
            sectionName: 'express'
          })}
          &nbsp;&nbsp;
        </Box>
        <Box
          display="flex"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          paddingLeft="8px"
        >
          <Box display="flex" alignItems="center" gap={0.75}>
            <ExpressSelectorOptionIcon width="14px" height="14px" />
            <Typography color="#FD1E1B" fontSize={12} fontWeight={700}>
              {t('unifiedBasket.title.3hours')}
            </Typography>
            <Typography color="secondary.main" fontSize={14} fontWeight={700}>
              {t('unifiedBasket.title.expressDelivery')}
            </Typography>
          </Box>
          <Box>
            <Box display="flex" alignItems="center" justifyContent="flex-end">
              <StyledBasketLabel>{t('label.deliveryFee')}:</StyledBasketLabel>
              &nbsp;
              <BasketDeliveryFee
                currentShippingFee={props.shippingFee}
                defaultShippingFee={props.originalShippingFee}
                priceUnit={props.priceUnit}
              />
            </Box>
            <Box display="flex" alignItems="center" fontSize="0.8rem">
              <StyledBasketLabel>{t('label.subtotal')}:</StyledBasketLabel>
              &nbsp;
              <Typography fontSize={12} fontWeight={400}>
                {formatPriceWithCurrency(props.subtotal, props.priceUnit)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </StyledBasketSellerHeader>
  );
};

BasketSellerHeader1P.displayName = 'BasketSellerHeader1P';
BasketSellerHeader3P.displayName = 'BasketSellerHeader3P';
